import React, { Component } from 'react'
import deepEqual from 'fast-deep-equal/react'
import { bindStateToProps, bindActionCreators } from 'lib'
import { connect } from 'react-redux'
import autoBind from 'auto-bind'
import { withRouter } from 'react-router-dom'
import qs from 'qs'
import Joi from 'joi-browser'
import pick from 'lodash/pick'
import pickBy from 'lodash/pickBy'
import isEqual from 'react-fast-compare'

import { actions as searchActions } from 'store/modules/search'
import { actions as snackbarActions } from 'store/modules/snackbar'
import { getResult } from 'lib/getSearchResult'
import { isObject, isFunction, isBoolean, isEmptyString } from 'lib/detectType'

import SearchForm from 'components/organisms/SearchForm'
import SnackbarContainer from 'containers/SnackbarContainer'

class SearchContainer extends Component {
  constructor(props) {
    super(props)
    autoBind(this)
    const { autoListen } = props
    if (autoListen === true) {
      this.props.history.listen((location, action) => {
        this.props.SearchActions.setItems({
          key: this.props.searchKey,
          value: qs.parse(location.search.replace(/^./, ''))
        })
      })
    }
  }

  componentDidMount = () => {
    const { searchKey: key, hash } = this.props
    if (hash[key] !== undefined) {
      const { query, schema = {}, result = {} } = this.props
      this.onInitCallback(result[key] || {}, query[key] || '')
      if (schema[key]) {
        this.validateItem(result[key], schema[key])
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { searchKey, items = {}, hash, query = {} } = this.props
    const {
      items: nextItems = {},
      hash: nextHash,
      query: nextQuery = {}
    } = nextProps
    if (hash !== nextHash) return true
    if (query[searchKey] !== nextQuery[searchKey]) return true
    return !deepEqual(items[searchKey], nextItems[searchKey])
  }

  componentDidUpdate = prevProps => {
    const { query: prevQuery, hash: prevHash } = prevProps
    const { query, searchKey: key, schema = {}, result = {}, hash } = this.props
    const initFlag = prevHash[key] !== hash[key]
    if (initFlag) {
      this.onInitCallback(result[key] || {}, query[key] || '')
      if (schema[key]) {
        this.validateItem(result[key], schema[key])
      }
    }
    if (prevQuery[key] !== query[key]) {
      if (!initFlag) {
        this.onSearchCallback(result[key] || {}, query[key] || '')
      }
      if (query[key] && schema[key]) {
        this.validateItem(result[key], schema[key])
      }
    }
  }

  componentWillUnmount = () => {
    const { SearchActions, searchKey } = this.props
    SearchActions.clear({ key: searchKey })
  }

  onInitCallback = (result, query) => {
    const { searchKey: key, onInitCallback, options = {} } = this.props
    const { onInitCallback: optionInitCallback } = options[key] || {}
    if (isFunction(onInitCallback)) {
      return onInitCallback(result, query)
    }
    if (isFunction(optionInitCallback)) {
      return optionInitCallback(result, query)
    }
  }

  onSearchCallback = (result, query) => {
    const { searchKey: key, onSearchCallback, options = {} } = this.props
    const { onSearchCallback: optionSearchCallback } = options[key] || {}
    if (isFunction(onSearchCallback)) {
      return onSearchCallback(result, query)
    }
    if (isFunction(optionSearchCallback)) {
      return optionSearchCallback(result, query)
    }
  }

  validateItem = (result, schema) => {
    const { error } = Joi.validate(pick(result, Object.keys(schema)), schema)
    if (error) {
      const { SnackbarActions } = this.props
      SnackbarActions.new({
        key: 'public',
        message: error.message
      })
      return false
    }
    return true
  }

  onChangeSearchCriteria(index, value) {
    const { SearchActions, searchKey: key } = this.props
    SearchActions.setCriteria({ key, index, value })
  }

  onChangeSearchValue(index, value) {
    const { SearchActions, searchKey: key, data = {}, items = {} } = this.props
    const criteria = items[key][index].criteria
    const { valueFormat } = data[key][criteria]
    if (isFunction(valueFormat)) {
      value = valueFormat(value)
    }
    SearchActions.setValue({ key, index, value })
  }

  onChangeSearchCondition(index, value) {
    const { SearchActions, searchKey: key } = this.props
    SearchActions.setCondition({ key, index, value })
  }

  onChangeDateRange(index, selectedDate) {
    const { SearchActions, searchKey, items = {}, data = {} } = this.props
    const { criteria } = items[searchKey][index]
    const { mapKeys } = data[searchKey][criteria]
    if (isObject(mapKeys) && Object.keys(mapKeys).length === 2) {
      const newItems = Object.assign(
        {},
        ...items[searchKey].map((data, idx) => {
          const value = isBoolean(data?.value)
            ? String(data?.value)
            : data?.value
          const formatValue = data?.condition
            ? { [data?.condition]: value }
            : value
          if (data?.criteria === criteria) {
            return { [data?.criteria]: selectedDate.join(',') }
          }
          return { [data?.criteria]: formatValue }
        })
      )
      const [startDateKey, endDateKey] = Object.keys(mapKeys)
      const { valueFormat: startFormat } = mapKeys[startDateKey]
      const { valueFormat: endFormat } = mapKeys[endDateKey]
      Object.assign(newItems, {
        [criteria]: selectedDate.join(','),
        [startDateKey]: isFunction(startFormat)
          ? startFormat(selectedDate[0])
          : selectedDate[0],
        [endDateKey]: isFunction(endFormat)
          ? endFormat(selectedDate[1])
          : selectedDate[1]
      })
      SearchActions.setItems({
        key: searchKey,
        value: newItems
      })
    }
  }

  onChangeDatePicker(index, selectedDate) {
    const { searchKey, items = {}, data = {} } = this.props
    const { criteria } = items[searchKey][index]
    const { startFormat, endFormat } = data[searchKey][criteria]
    this.onChangeSearchValue(index, [
      isFunction(startFormat) && selectedDate[0]
        ? startFormat(selectedDate[0])
        : selectedDate[0],
      isFunction(endFormat) && selectedDate[1]
        ? endFormat(selectedDate[1])
        : selectedDate[1]
    ])
  }

  onClickAddSearchItem() {
    const {
      SnackbarActions,
      SearchActions,
      searchKey: key,
      items,
      data
    } = this.props
    const criteria = Object.keys(
      pickBy(data[key], o => o.simpleField !== true)
    )[0]
    if (!criteria) return
    if (items[key].length < 10) return SearchActions.addItem({ key })
    return SnackbarActions.new({
      key,
      open: true,
      message:
        '더 이상 검색 조건을 추가할 수 없습니다. 검색 조건은 최대 10개 입니다.'
    })
  }

  onClickDeleteSearchItem(index) {
    const { SnackbarActions, SearchActions, searchKey: key, items } = this.props
    if (items[key].length > 1) return SearchActions.deleteItem({ key, index })
    return SnackbarActions.new({
      message:
        '더 이상 검색 조건을 삭제할 수 없습니다. 검색 조건은 최소 1개 이상 존재해야 합니다.'
    })
  }

  onSubmitSearch() {
    const {
      SearchActions,
      searchKey: key,
      result,
      data,
      items,
      condInfo,
      history,
      autoListen,
      schema = {}
    } = this.props
    const newResult = getResult(items[key], condInfo, data[key])
    const newQuery = qs.stringify(newResult, { arrayFormat: 'index' })
    if (
      isEqual(result[key], newResult) &&
      schema[key] &&
      !this.validateItem(newResult, schema[key])
    ) {
      return
    }
    SearchActions.submit({ key })
    if (
      autoListen === true &&
      !isEmptyString(newQuery) &&
      !isEqual(result[key], newResult)
    ) {
      // 검색 시 페이지 이동을 하지 않으려면 replace사용
      history.push(history.location.pathname + '?' + newQuery)
    }
  }

  onCancelSearch() {
    const { SearchActions, searchKey: key, history, autoListen } = this.props
    SearchActions.cancel({ key })
    if (autoListen === true) {
      history.push(history.location.pathname)
    }
  }

  render() {
    const { searchKey, options = {}, data = {}, items = {} } = this.props
    const { hide } = options[searchKey] || {}
    if (hide === true) return null
    return (
      <div>
        <SnackbarContainer snackbarKey={searchKey} />
        <SearchForm
          {...this.props}
          {...this.state}
          proxy={this}
          data={data[searchKey]}
          items={items[searchKey]}
          options={options[searchKey]}
        />
      </div>
    )
  }
}
export default connect(
  bindStateToProps({
    search: true
  }),
  bindActionCreators({
    snackbarActions,
    searchActions
  })
)(withRouter(SearchContainer))
